import { useState } from 'react';
import { suspendAccount } from '../../lib/api';
import { useApiMutation } from '../../hooks/useApi';
import type { Customer } from '../../types';
import Modal from '../Modal';

interface SuspendAccountProps {
  customer: Customer;
  onClose: () => void;
  onSuccess: () => void;
}

const suspendReasons = [
  'Suspicious activity',
  'Customer request',
  'Verification required',
  'Payment issues',
  'Policy violation',
  'Other',
];

const durationOptions = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'indefinite', label: 'Indefinite' },
];

export default function SuspendAccount({ customer, onClose, onSuccess }: SuspendAccountProps) {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('7d');
  const [notes, setNotes] = useState('');

  const { loading, error, execute } = useApiMutation(suspendAccount, {
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await execute({
      customerId: customer.id,
      customerEmail: customer.email,
      reason,
      duration,
      notes,
    });
  };

  return (
    <Modal title="Suspend Account" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              You are about to suspend the account for{' '}
              <strong>
                {customer.first_name} {customer.last_name}
              </strong>{' '}
              ({customer.email}).
            </p>
          </div>

          <div>
            <label className="label">Reason for Suspension</label>
            <select
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select a reason...</option>
              {suspendReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Duration</label>
            <select
              className="input"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
            >
              {durationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Additional Notes (Optional)</label>
            <textarea
              className="input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context..."
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-warning" disabled={loading || !reason}>
            {loading ? 'Suspending...' : 'Suspend Account'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
